import { Box, IconButton, Typography } from "@mui/material";
import { format } from "date-fns";
import { forwardRef, useMemo } from "react";
import { Availability, TimeSlot } from "src/api/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TimeSlotView from "../TimeSlotView";

interface Props3 {
  pickedDate: Date | null;
  availability: Availability;
  pickedTime: TimeSlot | null;
  onChange: (t: TimeSlot) => void;
  goBack: () => void;
}

const toKey = (d: Date) => format(d, "yyyy-MM-dd");

const TimeSlide = forwardRef<HTMLDivElement, Props3>(
  (
    { pickedTime, pickedDate, availability, onChange, goBack, ...rest },
    ref,
  ) => {
    const slots = useMemo(
      () => (pickedDate != null ? (availability[toKey(pickedDate)] ?? []) : []),
      [availability, pickedDate],
    );

    return (
      <Box
        ref={ref}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
        }}
        {...rest}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            pb: 1,
          }}
        >
          <IconButton size="small" onClick={goBack} aria-label="Change date">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" lineHeight={1.2}>
              {pickedDate ? format(pickedDate, "EEEE, MMMM d") : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {slots.length} {slots.length === 1 ? "time" : "times"} available
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 1.5,
            pb: 1.5,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            alignContent: "start",
          }}
        >
          {slots.map((t) => (
            <TimeSlotView
              timeSlot={t}
              key={t.start_at}
              currentValue={pickedTime}
              onChange={onChange}
            />
          ))}
          {slots.length === 0 && (
            <Typography
              color="text.secondary"
              sx={{ gridColumn: "1 / -1", p: 2 }}
            >
              No times left on this day. Go back and pick another date.
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);

export default TimeSlide;
